import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  AudioMutedOutlined,
  AudioOutlined,
  ExpandOutlined,
  MessageOutlined,
  PhoneFilled,
  ReloadOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';

// Fora do Modal o antd não propaga z-index: os popups sobem acima do widget (1500).
const POPUP_Z = 1510;
const COLLAPSE_AFTER_MS = 4000;
const noop = () => {};

function MiniButton({
  title,
  className,
  disabled,
  onClick,
  children,
}) {
  return (
    <Tooltip title={title} zIndex={POPUP_Z}>
      <span className="tmd-mini-btn-wrap">
        <button
          type="button"
          className={`tmd-mini-btn${className ? ` ${className}` : ''}`}
          aria-label={title}
          disabled={disabled}
          onClick={onClick}
        >
          {children}
        </button>
      </span>
    </Tooltip>
  );
}

// Card flutuante da chamada minimizada. O <video> daqui só lê o stream do
// #remoteVideo (que segue tocando o áudio dentro do modal escondido).
function MiniWidget({
  name,
  initials,
  avatar,
  hourLabel,
  status,
  connected,
  remoteCamOff,
  remoteMicMuted,
  micMuted,
  camOff,
  noAudioDevices,
  noVideoDevices,
  mediaReady,
  unread,
  ending,
  timedOut,
  isMobile,
  onToggleMic,
  onToggleCam,
  onOpenChat,
  onRestore,
  onRetry,
  onEnd,
}) {
  const thumbRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [settled, setSettled] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const showVideo = Boolean(connected && !remoteCamOff);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(true), COLLAPSE_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const thumb = thumbRef.current;
    const remote = document.getElementById('remoteVideo');
    if (!thumb || !remote || !showVideo || !remote.srcObject) {
      return undefined;
    }
    thumb.srcObject = remote.srcObject;
    const playing = thumb.play();
    if (playing && playing.catch) {
      playing.catch(noop);
    }
    return () => {
      thumb.srcObject = null;
    };
  }, [showVideo]);

  const needsAttention = status.tone === 'error' || unread > 0 || confirming;
  const collapsed = !isMobile && settled && !hovered && !needsAttention;
  const mediaLocked = !mediaReady || ending;

  return (
    <div
      className={`tmd-mini${collapsed ? ' tmd-mini-collapsed' : ''}`}
      role="dialog"
      aria-label="Teleconsulta minimizada"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      <div className="tmd-mini-head">
        <button
          type="button"
          className="tmd-mini-thumb"
          aria-label="Voltar para a teleconsulta"
          onClick={onRestore}
        >
          <video
            ref={thumbRef}
            className={showVideo ? 'tmd-mini-video' : 'tmd-mini-video tmd-mini-video-hidden'}
            muted
            playsInline
            autoPlay
          />
          {showVideo ? null : (
            <Avatar className="tmd-mini-avatar" size={40} src={avatar || undefined}>
              {initials}
            </Avatar>
          )}
          {connected && remoteMicMuted ? (
            <span className="tmd-mini-thumb-mic" aria-label="Paciente sem áudio">
              <AudioMutedOutlined />
            </span>
          ) : null}
        </button>

        <div className="tmd-mini-texts">
          <span className="tmd-mini-name">{name}</span>
          <span className="tmd-mini-status">
            <span className={`tmd-mini-dot tmd-mini-dot-${status.tone}`} />
            {status.label}
          </span>
          {hourLabel ? <span className="tmd-mini-detail">{hourLabel}</span> : null}
        </div>

        <MiniButton title="Expandir" className="tmd-mini-btn-primary tmd-mini-btn-sm" onClick={onRestore}>
          <ExpandOutlined />
        </MiniButton>
      </div>

      <div className="tmd-mini-controls" aria-hidden={collapsed}>
        <MiniButton
          title={noAudioDevices ? 'Nenhum microfone detectado' : 'Microfone'}
          className={micMuted || noAudioDevices ? 'tmd-mini-btn-off' : ''}
          disabled={noAudioDevices || mediaLocked}
          onClick={onToggleMic}
        >
          {micMuted || noAudioDevices ? <AudioMutedOutlined /> : <AudioOutlined />}
        </MiniButton>
        <MiniButton
          title={noVideoDevices ? 'Nenhuma câmera detectada' : 'Câmera'}
          className={camOff || noVideoDevices ? 'tmd-mini-btn-off' : ''}
          disabled={noVideoDevices || mediaLocked}
          onClick={onToggleCam}
        >
          <VideoCameraOutlined />
        </MiniButton>
        <Badge count={unread} size="small" offset={[-4, 4]}>
          <MiniButton title="Abrir o chat" onClick={onOpenChat}>
            <MessageOutlined />
          </MiniButton>
        </Badge>
        {timedOut ? (
          <MiniButton title="Tentar novamente" className="tmd-mini-btn-primary" onClick={onRetry}>
            <ReloadOutlined />
          </MiniButton>
        ) : null}
        <span className="tmd-mini-sep" />
        <Popconfirm
          title="Encerrar a teleconsulta?"
          description={connected
            ? 'A chamada será encerrada para você e para o paciente.'
            : 'A sala será fechada.'}
          okText="Encerrar"
          okButtonProps={{ danger: true }}
          cancelText="Voltar"
          placement="top"
          zIndex={POPUP_Z}
          disabled={ending}
          onOpenChange={setConfirming}
          onConfirm={onEnd}
        >
          <span className="tmd-mini-btn-wrap">
            <button
              type="button"
              className="tmd-mini-btn tmd-mini-btn-end"
              aria-label="Encerrar a teleconsulta"
              disabled={ending}
            >
              <PhoneFilled rotate={225} />
            </button>
          </span>
        </Popconfirm>
      </div>
    </div>
  );
}

export default MiniWidget;
